import React, { useState, useEffect } from 'react'
import { useUser } from 'util/UserContext'
import api from 'config/axiosConfig'
import { ReactComponent as UpvoteIcon } from 'assets/button-upvote.svg'
import { ReactComponent as DownvoteIcon } from 'assets/button-downvote.svg'
import { EntityType } from 'util/constants'
import { apiRoutes } from "util/apiRoutes";
import './Vote.css'

interface Props {
    entityId: number | string
    onVote?: () => void
    voteCount: number
    entityType: EntityType
    /**
     * When true, lays out the up/down arrows and count vertically with the
     * count between the arrows. Used by the media-viewer overlay where the
     * action bar runs along the right edge (Instagram-style).
     */
    vertical?: boolean
}

type VoteType = 'user_upvote' | 'user_downvote' | 'user_unvote'

const Vote = ({
    entityId,
    onVote,
    voteCount,
    entityType,
    vertical = false,
}: Props) => {
    const { currentUser, setShowAuthModal } = useUser();
    const [upvotes, setUpvotes] = useState<number>(0)
    const [downvotes, setDownvotes] = useState<number>(0)
    const [entityVoteCount, setVoteCount] = useState<number>(voteCount)
    const [userVote, setUserVote] = useState<VoteType | null>(null)

    useEffect(() => {
        const fetchVotes = async () => {
            try {
                if (currentUser) {
                    const response = await api.get<VoteType>(
                        apiRoutes.votes.userByEntity(entityType, entityId)
                    )
                    setUserVote(response.data)
                }
            } catch (error) {
                console.error('Error fetching votes:', error)
            }
        }

        fetchVotes()
    }, [entityId, entityType, currentUser])

    const handleVote = async (type: VoteType) => {
        if (!currentUser) {
            setShowAuthModal(true)
            return
        }

        try {
            let newVoteType: VoteType = type
            if (userVote === type) {
                newVoteType = 'user_unvote'
            }

            const voteValue = newVoteType === 'user_upvote' ? 1 : newVoteType === 'user_downvote' ? -1 : 0;

            await api.post(apiRoutes.votes.byEntity(entityType, entityId), { value: voteValue })

            if (newVoteType === 'user_upvote') {
                setUpvotes(upvotes + 1)
                if (userVote === 'user_downvote') {
                    setDownvotes(downvotes - 1)
                    setVoteCount(entityVoteCount + 2)
                } else {
                    setVoteCount(entityVoteCount + 1)
                }
            } else if (newVoteType === 'user_downvote') {
                setDownvotes(downvotes + 1)
                if (userVote === 'user_upvote') {
                    setUpvotes(upvotes - 1)
                    setVoteCount(entityVoteCount - 2)
                } else {
                    setVoteCount(entityVoteCount - 1)
                }
            } else {
                if (userVote === 'user_upvote') {
                    setUpvotes(upvotes - 1)
                    setVoteCount(entityVoteCount - 1)
                } else if (userVote === 'user_downvote') {
                    setDownvotes(downvotes - 1)
                    setVoteCount(entityVoteCount + 1)
                }
            }

            setUserVote(newVoteType === 'user_unvote' ? null : type)
            if (onVote) onVote()
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                setShowAuthModal(true)
            } else {
                console.error('Error voting:', error)
            }
        }
    }

    if (vertical) {
        // Instagram-style stacked layout: upvote button on top, count in
        // the middle, downvote button on the bottom. Used by the media
        // viewer's right-side action overlay.
        return (
            <div className="vote-container ignore-card-click flex flex-col items-center text-eoe-gray dark:text-eoe-dark-icon-stroke">
                <button
                    type="button"
                    onClick={() => handleVote('user_upvote')}
                    className={`vote-button ${userVote === 'user_upvote' ? 'active' : ''} flex items-center justify-center rounded-full p-1 hover:bg-white/15 transition-colors`}
                    aria-label="Upvote"
                >
                    <UpvoteIcon className="vote-icon size-9" />
                </button>
                <div className="vote-count text-xs font-semibold leading-none">
                    {entityVoteCount}
                </div>
                <button
                    type="button"
                    onClick={() => handleVote('user_downvote')}
                    className={`vote-button ${userVote === 'user_downvote' ? 'active downvote' : 'downvote'} flex items-center justify-center rounded-full p-1 hover:bg-white/15 transition-colors`}
                    aria-label="Downvote"
                >
                    <DownvoteIcon className="vote-icon size-9" />
                </button>
            </div>
        );
    }

    return (
        <div className={`${entityType !== 'comment' ? 'vote-container mt-auto h-9 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke flex justify-center items-center' : 'ignore-card-click vote-container rounded-full flex flex-row items-center'}`}>
            <div className={`${entityType !== 'comment' ? 'size-7 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors' : 'size-6 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors'}`}>
                <button
                    onClick={() => handleVote('user_upvote')}
                    className={`vote-button ${userVote === 'user_upvote' ? 'active' : ''}`}
                >
                    <UpvoteIcon className={`${entityType !== 'comment' ? 'vote-icon size-8' : 'vote-icon size-6'}`} />
                </button>
            </div>
            <div className={`vote-count text-center ${entityType !== 'comment' ? 'text-sm min-w-6 max-w-10' : 'text-xs w-6'}`}>
                <span className="font-semibold text-center text-eoe-gray dark:text-eoe-dark-icon-stroke">{entityVoteCount}</span>
            </div>
            <div className={`${entityType !== 'comment' ? 'size-7 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors' : 'size-6 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors'}`}>
                <button
                    onClick={() => handleVote('user_downvote')}
                    className={`vote-button ${userVote === 'user_downvote' ? 'active downvote' : 'downvote'}`}
                >
                    <DownvoteIcon className={`${entityType !== 'comment' ? 'vote-icon size-8' : 'vote-icon size-6'}`} />
                </button>
            </div>
        </div>
    )
}

export default Vote
